// File: src/pages/FounderJourney.jsx

import React, { useRef, useEffect, useState } from "react";
import CompanyHeader from "./CompanyHeader";
import FooterLinks from "../LandingPage/FooterLinks";
import SeoMeta from "../SEO/SeoMeta";

export default function FounderJourney() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    function updateHeight() {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-white text-black">
      <SeoMeta
        title="Founders' Journey — A.G. Senthil Kumar & Satbir Singh Hanspaul(Hanspaul Group CEO)| Million2Trillion"
        description="Discover how A.G. Senthil Kumar and Satbir Singh Hanspaul(Hanspaul Group CEO)   united financial integrity and industrial leadership to create Million2Trillion—a verified, global ecosystem for market professionals and investors."
        keywords="Million2Trillion Founders, Senthil Kumar, Satbir Hanspaul, Fintech Leadership, Financial Network, Verified Market Platform"
        url="https://million2trillion.com/founderjourney"
      />

      <CompanyHeader ref={headerRef} activePage="founderjourney" />
      <div style={{ height: headerHeight }} aria-hidden="true" />

      {/* Banner */}
      <div className="w-full bg-white">
        <img
          src="/assets/company/Million2Trillion.gif"
          alt="About Million2Trillion"
          className="block w-full"
          style={{ width: "100vw", maxWidth: "100vw", height: "auto" }}
          draggable={false}
        />
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl space-y-20 px-4 pb-20 pt-12">
        <header className="space-y-4 text-center">
          <h1 className="text-4xl font-extrabold text-black md:text-5xl">
            Founders’ Journey: From Market Reality to Financial Revolution
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-700 md:text-2xl">
            Million2Trillion — Built on Experience, Driven by Integrity,
            Designed for the Verified Future of Global Finance
          </p>
        </header>

        {/* Side-by-Side Founders (Satbir LEFT, Senthil RIGHT) */}
        <section className="flex flex-col items-start justify-between gap-12 md:flex-row">
          {/* LEFT — SATBIR SINGH HANSPAUL */}
          <div className="flex-1 space-y-6 text-justify text-lg text-gray-800">
            <div className="flex flex-col items-center">
              <img
                src="/assets/team/Satbir-cofounder.jpg"
                alt="Co-Founder Satbir Singh Hanspaul – Million2Trillion"
                className="mb-6 h-[20rem] w-[20rem] rounded-full border-4 border-purple-400 object-cover shadow"
              />
              <blockquote className="mx-auto mb-6 max-w-2xl text-center text-xl font-semibold italic text-purple-700">
                “Finance is not about chasing returns — it’s about creating
                systems that earn trust for generations.”
              </blockquote>
            </div>

            <p className="mb-2 text-center text-xl font-bold text-purple-700">
              Satbir Singh Hanspaul — Finance, Strategy & Scale
            </p>

            <p>
              <strong>Satbir Singh Hanspaul</strong> is the Co-Founder &
              President of <strong>Million2Trillion</strong>, a next-generation
              financial market ecosystem redefining transparency, verification,
              and accessibility in global finance. His role centers on{" "}
              <strong>Finance, Strategy, and Scale</strong>, driving the
              platform’s evolution from concept to institution.
            </p>

            <p>
              As the <strong>Group CEO of Hanspaul Group Tanzania</strong>,
              Satbir leads one of East Africa’s most respected diversified
              conglomerates, spanning automotive manufacturing, packaging,
              construction, financial services, and real estate. Under his
              stewardship, Hanspaul Group has become a model of structured
              growth and operational excellence — earning both the{" "}
              <strong>
                President’s Manufacturer of the Year Award (PMAYA)
              </strong>{" "}
              for 14 consecutive years and the{" "}
              <strong>Africa Company of the Year Award (ACOYA 2023)</strong>.
            </p>

            <p>
              A graduate in <strong>Accounting and Finance (BSc)</strong> from
              the
              <strong> University of Birmingham (UK)</strong>, Satbir combines
              analytical rigor with entrepreneurial foresight. His approach to
              finance blends macro-economic insight with scalable structure —
              building systems that sustain growth beyond market cycles and
              leadership tenures.
            </p>

            <p>
              Within <strong>Million2Trillion</strong>, Satbir’s influence
              anchors the enterprise in fiscal discipline and institutional
              integrity. He ensures every innovation within the platform aligns
              with real-world value creation and measurable economic stability.
              His philosophy remains clear:{" "}
              <em>capital must serve people before it multiplies.</em>
            </p>

            <p>
              Beyond corporate leadership, Satbir is a strong advocate of{" "}
              <strong>responsible capitalism</strong> — advancing initiatives
              such as <strong>E-Motion Africa</strong>, a pioneering
              EV-conversion and sustainability program promoting green
              industrial transformation. His strategic vision connects finance,
              technology, and impact, ensuring that progress remains both
              inclusive and enduring.
            </p>

            <p>
              Satbir is also a proud member of the{" "}
              <strong>Young Presidents’ Organization (YPO)</strong>, where he
              engages with global leaders to exchange insights on innovation,
              governance, and transformative growth.
            </p>

            <p>
              Through his combined roles in industry and fintech,{" "}
              <strong>Satbir Singh Hanspaul</strong> represents a new archetype
              of financial leadership — one that connects industrial strength
              with digital trust, guiding
              <strong> Million2Trillion</strong> toward becoming a verified,
              scalable, and sustainable global financial ecosystem.
            </p>
          </div>

          {/* RIGHT — Founder Senthil Kumar */}
          <div className="flex-1 space-y-6 text-justify text-lg text-gray-800">
            <div className="flex flex-col items-center">
              <img
                src="/assets/team/Senthil-founder4.jpg"
                alt="Founder & CEO Senthil Kumar – Million2Trillion"
                className="mb-6 h-[20rem] w-[20rem] rounded-full border-4 border-purple-400 object-cover shadow"
              />
              <blockquote className="mx-auto mb-6 max-w-2xl text-center text-xl font-semibold italic text-purple-700">
                “Markets reward precision, discipline, and trust —
                Million2Trillion was built to protect all three.”
              </blockquote>
            </div>

            <p className="mb-2 text-center text-xl font-bold text-purple-700">
              A.G. Senthil Kumar — Financial Market Architect & Engineer
            </p>

            <p>
              <strong>Founder & CEO Senthil Kumar</strong>, the visionary behind{" "}
              <strong>Million2Trillion</strong>, is a seasoned market expert and
              financial systems engineer who transformed over a decade of
              trading experience into a mission to build the world’s most
              trusted financial market ecosystem. With a deep passion for
              markets, he has lived every single day as a trader and investor
              across{" "}
              <strong>
                forex, stocks, commodities, binary options, and cryptocurrencies
              </strong>{" "}
              — experiencing first-hand the volatility, manipulation, and scams
              that many professionals face.
            </p>

            <p>
              These real-world lessons didn’t discourage him — they defined his
              purpose. Combining his
              <strong> engineering expertise</strong> with disciplined market
              logic, Founder Senthil Kumar set out to build a{" "}
              <strong>verified, transparent, and scam-free platform</strong>{" "}
              that protects millions of global users while empowering
              professionals to perform with authenticity and proof. His work
              bridges technology and trust — where every algorithm, record, and
              transaction serves one goal: protecting the financial dignity of
              every participant.
            </p>

            <p>
              Through <strong>Million2Trillion (M2T)</strong>, he is redefining
              how the world perceives financial markets — transforming chaos
              into clarity through technology, verification, and compliance.
              Every layer of M2T’s infrastructure — from SmartCard identity
              systems to blockchain-based verification engines — is coded and
              architected directly under his leadership, ensuring unmatched
              <strong> security, reliability, and authenticity</strong>.
            </p>

            <p>
              An engineer by training and a trader by conviction,{" "}
              <strong>Founder Senthil Kumar</strong> combines precision in code
              with discipline in finance — translating complex market behavior
              into secure, verifiable systems. His hands-on development approach
              ensures that M2T’s foundation is not theoretical but practical —
              shaped by years of lived trading experience and guided by the
              mission to make markets safe, verified, and transparent for all.
            </p>

            <p>
              Today, building one of the world’s most
              <strong>
                {" "}
                powerful, secured, and zero-scam financial infrastructures
              </strong>{" "}
              — a system designed to protect every participant and bring back{" "}
              <strong>trust, transparency, and performance truth </strong>
              to global finance. He believes that markets were never truly
              broken — only disconnected. Through
              <strong> Million2Trillion</strong>, he is reconnecting those
              fragmented systems into one verified, measurable network where
              every user is real, every transaction is authentic, and every
              success is earned through trust.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="space-y-6 border-t pt-12 text-center">
          <h3 className="text-3xl font-extrabold text-purple-600">
            Together, Building the Future of Verified Global Finance
          </h3>

          <p className="mx-auto max-w-3xl text-lg text-gray-700">
            When <strong>Co-Founder Satbir Singh Hanspaul</strong> transformed
            industrial excellence into sustainable capital, and{" "}
            <strong>Founder A.G. Senthil Kumar</strong> converted market pain
            into verified architecture — their shared purpose gave rise to
            <strong> Million2Trillion</strong>.
          </p>

          <p className="mx-auto max-w-2xl text-lg text-gray-700">
            Two leaders. One equation. <br />
            <span className="font-semibold text-purple-700">
              Trust × Transparency = Transformation.
            </span>
          </p>

          <p className="mx-auto max-w-3xl text-lg text-gray-700">
            Together, they are building the world’s first verified financial
            market ecosystem — connecting
            <strong>
              {" "}
              traders, investors, educators and all market roles
            </strong>{" "}
            under a single framework of
            <span className="font-semibold text-purple-700">
              {" "}
              truth, technology, and trust
            </span>
            .
          </p>

          <p className="text-base font-semibold text-black">
            — The Million2Trillion Story
          </p>
        </section>

        {/* Contact */}
        <section className="mt-10 border-t pt-10 text-center">
          <h2 className="mb-2 text-2xl font-bold text-purple-500">
            Contact Founders
          </h2>
          <p className="mb-3">
            For media, partnerships, or collaborations:
            <br />
            <a
              href="mailto:founder@million2trillion.com"
              className="block text-purple-700 underline"
            >
              founder@million2trillion.com
            </a>
            <a
              href="mailto:cofounder@million2trillion.com"
              className="block text-purple-700 underline"
            >
              cofounder@million2trillion.com
            </a>
          </p>
        </section>
      </main>

      <footer className="mt-36 border-t border-gray-700 bg-black">
        <FooterLinks />
      </footer>
    </div>
  );
}
